import { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';

import { useReorderableOrders } from 'hooks/use-reorderable-orders';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useOrderContext } from 'state/order';
import { IPendingReorder } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { isOfferType } from '../utils';

export const useReorder = (order?: IPendingReorder | null, url = routes.orders) => {
  const {
    reorder: { handleReorder, setPendingReorder, setShouldHandleReorder },
    isPreConfirmCurbside,
  } = useOrderContext();
  const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const { isDelivery } = useServiceModeContext();
  const { setShouldShowPreConfirmCurbsideModal } = useUIContext();
  const enableReorder = useFlag(LaunchDarklyFlag.ENABLE_REORDER);
  const router = useRouter();
  const { makeOrderReorderable } = useReorderableOrders();

  // Temporary flag to disable reordering of items/combos based on Sanity IDs. Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const isOrderExcludedFromReordering = useMemo(() => {
    if (!order?.cart.cartEntries.length) {
      return true;
    }

    if (order.cart.cartEntries.length === 1) {
      return (sanityIdsExcludedFromReordering ?? []).includes(order.cart.cartEntries[0].sanityId);
    }

    return order.cart.cartEntries.some(entry => isOfferType(entry.type));
  }, [order, sanityIdsExcludedFromReordering]);

  const handleReorderClick = useCallback(() => {
    if (!order) {
      return;
    }

    if (isPreConfirmCurbside()) {
      setShouldShowPreConfirmCurbsideModal(true);
      return;
    }

    const reorderableOrder = order.cart.reorderCartEntries ? order : makeOrderReorderable(order);

    if (isStoreOpenAndAvailable) {
      handleReorder(reorderableOrder, url);
    } else {
      setPendingReorder(reorderableOrder);
      setShouldHandleReorder(true);
      router.replace(isDelivery ? routes.deliveryAddress : routes.storeLocator);
    }
  }, [
    isPreConfirmCurbside,
    order,
    isStoreOpenAndAvailable,
    setShouldShowPreConfirmCurbsideModal,
    handleReorder,
    url,
    setPendingReorder,
    setShouldHandleReorder,
    router,
    isDelivery,
    makeOrderReorderable,
  ]);

  const isReorderingDisabled = useMemo(() => {
    const isSelectedStoreNotOpenAndAvailable = !isStoreOpenAndAvailable && !noStoreSelected;

    return !enableReorder || isSelectedStoreNotOpenAndAvailable || isOrderExcludedFromReordering;
  }, [enableReorder, isStoreOpenAndAvailable, noStoreSelected, isOrderExcludedFromReordering]);

  return { handleReorderClick, isReorderingDisabled };
};
